// src/pages/ActivitiesBuilder/index.tsx
// src/pages/ActivitiesBuilder/index.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Divider,
  Button,
  CircularProgress,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Search as SearchIcon,
  AutoAwesome as GenerateIcon,
  Bookmark as SavedIcon
} from '@mui/icons-material';

// Components
import TripSelector from '../../components/itinerary/TripSelector';
import ActivitySearch from './components/ActivitySearch';
import ActivityGenerate from './components/ActivityGenerate';
import SavedActivities from './components/SavedActivities';

// Contexts
import { ActivityProvider, useActivity } from '../../contexts/ActivityContext';
import { ItineraryProvider, useItinerary, Trip } from '../../contexts/ItineraryContext';

// Tabs interface
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// Tab panel component
function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`activities-tabpanel-${index}`}
      aria-labelledby={`activities-tab-${index}`}
      {...other}
      style={{ height: '100%' }}
    >
      {value === index && (
        <Box sx={{ p: 1, height: '100%' }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// Tab accessibility props
function a11yProps(index: number) {
  return {
    id: `activities-tab-${index}`,
    'aria-controls': `activities-tabpanel-${index}`,
  };
}

// Wrapper component with providers - IMPORTANT: Order matters here!
// ItineraryProvider must be outside ActivityProvider since Activity uses Itinerary context
const ActivitiesBuilderWithProvider: React.FC = () => {
  return (
    <ItineraryProvider>
      <ActivityProvider>
        <ActivitiesBuilderContent />
      </ActivityProvider>
    </ItineraryProvider>
  );
};

// Main component content
const ActivitiesBuilderContent: React.FC = () => {
  // Get trip ID from URL params
  const { tripId } = useParams<{ tripId: string }>();
  const navigate = useNavigate();
  
  // Get trips from itinerary context
  const { trips, selectedTrip, setSelectedTrip, loading: tripsLoading } = useItinerary();
  
  // Get activities context
  const { 
    savedActivities,
    fetchActivities, 
    loading: activitiesLoading,
    error
  } = useActivity();
  
  // Component state
  const [tabValue, setTabValue] = useState(0);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Load trip from URL param
  useEffect(() => {
    if (tripId) {
      const trip = trips.find(t => t.id === tripId);
      if (trip) {
        setSelectedTrip(trip);
      }
    }
  }, [tripId, trips, setSelectedTrip]);
  
  // Load activities when trip is selected
  useEffect(() => {
    if (selectedTrip) {
      fetchActivities(selectedTrip.id);
    }
  }, [selectedTrip, fetchActivities]);
  
  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  // Select a trip
  const handleSelectTrip = (trip: Trip | null) => {
    setSelectedTrip(trip);
    if (trip) {
      navigate(`/activities/${trip.id}`);
    }
  };
  
  // Close success message
  const handleCloseSnackbar = () => {
    setSuccessMessage(null);
  };
  
  // Show success message
  const showSuccess = (message: string) => {
    setSuccessMessage(message);
  };
  
  return (
    <Box 
      sx={{ 
        height: 'calc(100vh - 100px)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      <Typography variant="h4" gutterBottom>
        Activities Builder
      </Typography>
      
      {/* Trip Selector */}
      <Box sx={{ mb: 3 }}>
        <TripSelector
          trips={trips}
          selectedTrip={selectedTrip}
          loading={tripsLoading}
          onSelectTrip={handleSelectTrip}
        />
      </Box>
      
      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {/* Main Content */}
      {selectedTrip && (
        <Box sx={{ 
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          <Paper sx={{ 
            mb: 2, 
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            height: '100%'
          }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs 
                value={tabValue} 
                onChange={handleTabChange} 
                aria-label="activity builder tabs"
                sx={{ '& .MuiTab-root': { py: 2 } }}
              >
                <Tab 
                  icon={<SearchIcon />} 
                  label="Search Activities" 
                  {...a11yProps(0)} 
                  sx={{ minHeight: 72 }} 
                />
                <Tab 
                  icon={<GenerateIcon />} 
                  label="Generate with AI" 
                  {...a11yProps(1)} 
                  sx={{ minHeight: 72 }} 
                />
                <Tab 
                  icon={<SavedIcon />} 
                  label={`Saved Activities (${savedActivities.length})`} 
                  {...a11yProps(2)} 
                  sx={{ minHeight: 72 }} 
                />
              </Tabs>
            </Box>
            
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
              {/* Loading Indicator */}
              {activitiesLoading && (
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center',
                  height: '100%'
                }}>
                  <CircularProgress />
                </Box>
              )}

              {/* Tab Panels */}
              <TabPanel value={tabValue} index={0}>
                <ActivitySearch 
                  trip={selectedTrip} 
                  onSuccess={showSuccess} 
                />
              </TabPanel>
              
              <TabPanel value={tabValue} index={1}>
                <ActivityGenerate 
                  trip={selectedTrip} 
                  onSuccess={showSuccess} 
                />
              </TabPanel>
              
              <TabPanel value={tabValue} index={2}>
                <SavedActivities 
                  trip={selectedTrip}
                  onSuccess={showSuccess} 
                />
              </TabPanel>
            </Box>
          </Paper>
        </Box>
      )}
      
      {/* Success Message */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        message={successMessage}
      />
    </Box>
  );
};

export default ActivitiesBuilderWithProvider;